import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard, fmtEUR } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const STATUS_BADGE = {
  understaffed: "bg-red-100 text-red-800 border border-red-300",
  adequate: "bg-emerald-100 text-emerald-800 border border-emerald-300",
  overstaffed: "bg-amber-100 text-amber-800 border border-amber-300",
  not_configured: "bg-neutral-100 text-neutral-500 border border-neutral-300",
};
const STATUS_LABEL = { understaffed: "Sotto organico", adequate: "Adeguato", overstaffed: "Sovra organico", not_configured: "Da configurare" };

export default function StaffingForecast() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [prod, setProd] = useState({ bar: "", sala: "", cucina: "" });
  const [savingProd, setSavingProd] = useState(false);

  const load = async () => {
    const [r, settings] = await Promise.all([
      api.get(`/staffing/forecast?days=${days}`),
      api.get("/settings"),
    ]);
    setData(r.data);
    const p = (settings.data.operations || {}).staffing?.productivity || {};
    setProd({ bar: p.bar ?? "", sala: p.sala ?? "", cucina: p.cucina ?? "" });
  };
  useEffect(() => { load(); }, [days]);

  const saveProductivity = async () => {
    setSavingProd(true);
    try {
      const settings = (await api.get("/settings")).data;
      const cur = settings.operations || {};
      const staffing = cur.staffing || {};
      staffing.productivity = {
        bar: prod.bar === "" ? null : Number(prod.bar),
        sala: prod.sala === "" ? null : Number(prod.sala),
        cucina: prod.cucina === "" ? null : Number(prod.cucina),
      };
      cur.staffing = staffing;
      cur.seasonality_coefficients = cur.seasonality_coefficients ||
        Object.fromEntries(Array.from({ length: 12 }, (_, i) => [`${String(i + 1).padStart(2, "0")}`, null]));
      await api.put("/settings/operations", cur);
      toast.success("Produttività salvata"); load();
    } catch (e) { toast.error("Errore salvataggio"); }
    setSavingProd(false);
  };

  if (!data) return <div className="p-6">Caricamento…</div>;

  return (
    <div data-testid="staffing-page">
      <PageHeader eyebrow="Operatività" title="Fabbisogno Personale"
        subtitle="Suggerimenti per fascia oraria. Il sistema non modifica turni automaticamente." />

      {/* Configurazione produttività */}
      <div className="border border-[#E2D9C8] rounded-lg p-6 bg-white mb-6" data-testid="prod-config">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h3 className="font-serif text-xl">Produttività target</h3>
            <div className="text-xs text-neutral-500">Transazioni/coperti orari per addetto</div>
          </div>
          {!data.productivity_configured && (
            <span className="text-xs bg-amber-100 text-amber-900 px-2 py-1 rounded border border-amber-300">Da configurare</span>
          )}
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div>
            <div className="label-eyebrow mb-1">Bar (tx/h)</div>
            <Input type="number" value={prod.bar} onChange={(e) => setProd({ ...prod, bar: e.target.value })} data-testid="prod-bar" />
          </div>
          <div>
            <div className="label-eyebrow mb-1">Sala (coperti/h)</div>
            <Input type="number" value={prod.sala} onChange={(e) => setProd({ ...prod, sala: e.target.value })} data-testid="prod-sala" />
          </div>
          <div>
            <div className="label-eyebrow mb-1">Cucina (ordini/h)</div>
            <Input type="number" value={prod.cucina} onChange={(e) => setProd({ ...prod, cucina: e.target.value })} data-testid="prod-cucina" />
          </div>
          <div className="flex items-end">
            <Button onClick={saveProductivity} disabled={savingProd} className="bg-neutral-900 text-[#FDFBF7]" data-testid="save-prod">
              Salva
            </Button>
          </div>
        </div>
        <div className="text-xs text-neutral-500 mt-3">Costo medio orario dipendenti configurato: <b>{fmtEUR(data.avg_hourly_rate)}</b>/h</div>
      </div>

      <div className="flex justify-between items-center mb-4">
        <div className="text-sm">Orizzonte:</div>
        <div className="flex gap-2">
          {[1, 3, 7].map((d) => (
            <Button key={d} variant={days === d ? "default" : "outline"} onClick={() => setDays(d)} data-testid={`btn-days-${d}`}>
              {d === 1 ? "Oggi" : d === 3 ? "3 giorni" : "7 giorni"}
            </Button>
          ))}
        </div>
      </div>

      {data.days.map((day) => (
        <div key={day.date} className="mb-6 border border-[#E2D9C8] rounded-lg bg-white overflow-hidden" data-testid={`day-${day.date}`}>
          <div className="p-4 bg-[#FDFBF7] border-b border-[#E2D9C8]">
            <div className="font-serif text-lg">{day.weekday} · {day.date}</div>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="text-left label-eyebrow">
              <th className="p-3">Fascia</th>
              <th className="p-3 text-right">Vendite previste</th>
              <th className="p-3 text-right">Tx previste</th>
              <th className="p-3">Area</th>
              <th className="p-3 text-right">Pianificati</th>
              <th className="p-3 text-right">Consigliati</th>
              <th className="p-3 text-right">Δ</th>
              <th className="p-3 text-right">Costo Δ</th>
              <th className="p-3">Stato</th>
            </tr></thead>
            <tbody>
              {day.slots.map((slot) => (
                Object.entries(slot.areas).map(([area, info], idx) => (
                  <tr key={`${slot.slot}-${area}`} className="border-t border-[#E2D9C8]">
                    {idx === 0 && <td rowSpan={3} className="p-3 align-top">
                      <div className="font-medium">{slot.slot}</div>
                      <div className="text-[10px] text-neutral-400">conf: {slot.confidence}</div>
                    </td>}
                    {idx === 0 && <td rowSpan={3} className="p-3 text-right align-top">{fmtEUR(slot.predicted.predicted_revenue)}</td>}
                    {idx === 0 && <td rowSpan={3} className="p-3 text-right align-top">{slot.predicted.predicted_transactions}</td>}
                    <td className="p-3 capitalize">{area}</td>
                    <td className="p-3 text-right">{info.planned}</td>
                    <td className="p-3 text-right">{info.recommended === null ? "—" : info.recommended}</td>
                    <td className={`p-3 text-right ${info.delta > 0 ? "text-red-700" : info.delta < 0 ? "text-amber-700" : ""}`}>
                      {info.delta === null ? "—" : (info.delta > 0 ? `+${info.delta}` : info.delta)}
                    </td>
                    <td className="p-3 text-right">{info.cost_delta === null ? "—" : fmtEUR(info.cost_delta)}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded text-xs ${STATUS_BADGE[info.status]}`}>{STATUS_LABEL[info.status]}</span>
                    </td>
                  </tr>
                ))
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <div className="text-xs text-neutral-500 mt-6">{data.note}</div>
    </div>
  );
}
